import { useEffect, useState } from "react";

/**
 * Temporary on-device diagnostic, shown only when the URL has `?debug=1`.
 *
 * It captures raw `keydown` events at the window (capture phase, so it sees them even
 * when an input is focused) and also subscribes to the ScannerBridge. This distinguishes:
 *  - keystrokes never reaching the page (DataWedge not in keystroke mode / not targeting
 *    the browser) → "keydowns" stays at 0;
 *  - keystrokes arriving but no scan firing (focus in an input, buffering, etc.) →
 *    "keydowns" climbs but "scans" stays at 0;
 *  - working end to end → both climb and "last scan" shows the decoded value.
 */
export function ScanDebug() {
  const [keys, setKeys] = useState({ count: 0, last: "" });
  const [scans, setScans] = useState({ count: 0, last: "" });

  useEffect(() => {
    let buf = "";
    const onKey = (e: KeyboardEvent) => {
      buf = (buf + (e.key.length === 1 ? e.key : `<${e.key}>`)).slice(-70);
      setKeys((k) => ({ count: k.count + 1, last: buf }));
    };
    window.addEventListener("keydown", onKey, true);

    let unsub: (() => void) | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const trySubscribe = () => {
      if (window.ScannerBridge?.subscribe) {
        unsub = window.ScannerBridge.subscribe((ev) => {
          setScans((s) => ({ count: s.count + 1, last: ev.rawBarcode }));
        });
      } else {
        timer = setTimeout(trySubscribe, 100);
      }
    };
    trySubscribe();

    return () => {
      window.removeEventListener("keydown", onKey, true);
      if (timer) clearTimeout(timer);
      unsub?.();
    };
  }, []);

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        background: "#0f172a",
        color: "#67e8f9",
        fontSize: 11,
        lineHeight: 1.4,
        fontFamily: "monospace",
        padding: "4px 8px",
        borderBottom: "1px solid #334155",
        wordBreak: "break-all",
      }}
    >
      <div>keydowns: {keys.count} · {keys.last || "—"}</div>
      <div>scans: {scans.count} · {scans.last ? scans.last.slice(0, 60) : "—"}</div>
    </div>
  );
}
